import { useRuntimeConfig } from '#imports'

import type { Market } from '@backmarket/http-api'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import type { RouteRecordRaw } from 'vue-router'

import type { Features } from '../../features'

import { CMS } from './routes-names'

const buybackRoutes: RouteRecordRaw[] = [
  {
    name: CMS.BUYBACK,
    path: '/:locale/buyback/:pageName',
    component: () => import('./Landing.vue'),
    meta: {
      disableClientSideNavigation: () => true,
    },
  },
  {
    name: CMS.BUYBACK_CATEGORY,
    path: '/:locale/buyback/:pageName/:pageCategory',
    component: () => import('./Landing.vue'),
    meta: {
      disableClientSideNavigation: () => true,
    },
  },
]

export default ({
  features,
  market,
}: {
  features: Features
  market: Market
}): RouteRecordRaw[] => {
  const {
    FF_ENABLED_COUNTRIES_FRONT_APPS_ROUTE_GIFT_FINDER_PAGES = '',
    FF_ENABLED_COUNTRIES_FRONT_APPS_ROUTE_GIFT_FINDER_RESULTS = '',
    FF_ENABLED_COUNTRIES_FRONT_APPS_ROUTE_EVENT_PAGES = '',
    FF_ENABLED_FRONT_APPS_EVENT_PAGE_NAMES = '',
    FF_ENABLED_COUNTRIES_FRONT_APPS_ROUTE_BRAND_SHOPS = '',
    FF_ENABLED_FRONT_APPS_EVENT_PAGE_ALL = false,
  } = useRuntimeConfig().public

  const isBuybackEnabled = features.buyback?.enabled

  return [
    {
      name: CMS.ARTICLE_ROOT_HUB,
      path: '/:locale/c',
      component: () => import('~/scopes/article/Hub/RootHub.vue'),
      meta: {
        disableClientSideNavigation: () => true,
      },
    },
    {
      name: CMS.ARTICLE_HUB,
      path: '/:locale/c/hub/:unifiedName',
      component: () => import('~/scopes/article/Hub/Hub.vue'),
      meta: {
        disableClientSideNavigation: () => true,
      },
    },
    {
      name: CMS.ARTICLE,
      path: '/:locale/c/:category/:pageName',
      component: () => import('~/scopes/article/Article/Article.vue'),
      meta: {
        disableClientSideNavigation: () => true,
      },
    },

    {
      name: CMS.BRAND_STORE,
      path: '/:locale/b/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        disableClientSideNavigation: () => {
          const isOpenInFrontApps =
            FF_ENABLED_COUNTRIES_FRONT_APPS_ROUTE_BRAND_SHOPS.includes(
              market.countryCode,
            )

          return !isOpenInFrontApps
        },
      },
    },
    {
      name: CMS.BRAND_STORE_CATEGORY,
      path: '/:locale/b/:pageName/:pageCategory',
      component: () => import('./Landing.vue'),
      meta: {
        disableClientSideNavigation: () => {
          const isOpenInFrontApps =
            FF_ENABLED_COUNTRIES_FRONT_APPS_ROUTE_BRAND_SHOPS.includes(
              market.countryCode,
            )

          return !isOpenInFrontApps
        },
      },
    },

    // conditional CMS.BUYBACK routes
    ...insertIf(isBuybackEnabled, buybackRoutes),

    {
      name: CMS.EVENT,
      path: '/:locale/e/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        disableClientSideNavigation: ({ to }) => {
          const allowFrontAppsVersionInCountry =
            FF_ENABLED_COUNTRIES_FRONT_APPS_ROUTE_EVENT_PAGES.includes(
              market.countryCode,
            )
          const allowFrontAppsVersionForPageName =
            FF_ENABLED_FRONT_APPS_EVENT_PAGE_ALL ||
            FF_ENABLED_FRONT_APPS_EVENT_PAGE_NAMES.split(',')
              .map((item) => item.trim())
              .includes(to.params.pageName)

          const allowClientSideNavigation =
            allowFrontAppsVersionInCountry && allowFrontAppsVersionForPageName

          return !allowClientSideNavigation
        },
      },
    },

    {
      name: CMS.GIFT_FINDER_MAIN,
      path: '/:locale/gift/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        disableClientSideNavigation: () => {
          const isGiftFinderOpenInFrontApps =
            FF_ENABLED_COUNTRIES_FRONT_APPS_ROUTE_GIFT_FINDER_PAGES.includes(
              market.countryCode,
            )

          return !isGiftFinderOpenInFrontApps
        },
      },
    },
    {
      name: CMS.GIFT_FINDER_EVENT,
      path: '/:locale/gift/:pageName/:pageCategory',
      component: () => import('./Landing.vue'),
      meta: {
        disableClientSideNavigation: () => {
          const areGiftFinderResultsOpenInFrontApps =
            FF_ENABLED_COUNTRIES_FRONT_APPS_ROUTE_GIFT_FINDER_RESULTS.includes(
              market.countryCode,
            )

          return !areGiftFinderResultsOpenInFrontApps
        },
      },
    },
    {
      name: CMS.SELLER,
      path: '/:locale/seller/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        disableClientSideNavigation: () => true,
      },
    },

    {
      name: CMS.SERVICE,
      path: '/:locale/service/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        disableClientSideNavigation: () => false,
      },
    },

    {
      name: CMS.STATEMENT,
      path: '/:locale/accessibility-statement',
      component: () => import('./Landing.vue'),
      meta: {
        disableClientSideNavigation: () => true,
      },
    },

    {
      name: CMS.THE_X,
      path: '/:locale/x/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        disableClientSideNavigation: () => true,
      },
    },

    {
      name: CMS.E2E_PAGE,
      path: '/:locale/e2e/:pageName',
      component: () => import('./Landing.vue'),
      meta: {
        disableClientSideNavigation: () => true,
      },
    },
  ]
}
